/**
 * @description Reporting v1 Financial tab — MRR, New, Churn, product/service split.
 * @dependencies reportingService, formatCurrency, getApiErrorMessage
 * @notes GAAP income-statement / balance-sheet / trial-balance routes stay mounted
 * unused. Product vs Service uses /reports/revenue-split when present.
 * Both endpoints apply the same resolveMetricWindow (six-month default,
 * 36-month clamp) so a long picker cannot make split out-range MRR.
 * Recurring metrics include a reconstructed monthly MRR trend; last month is as of To.
 * Product vs Service bars use the same brand/service colors as the legend.
 * Date-range reloads clear metrics and split so cards and the chart never
 * keep the previous window while the new one loads. A metrics or split
 * failure must not look like an empty window.
 */

import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import reportingService from '../services/api/reportingService';
import { formatCurrency } from '../utils/invoiceHelpers';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import {
  CHART,
  ChartCard,
  ChartEmpty,
  ChartLegend,
  ReportingBarChart,
  ReportingLineChart
} from './reporting/ReportingCharts';

function lastSixMonthsRange(today = new Date()) {
  return {
    dateFrom: format(new Date(today.getFullYear(), today.getMonth() - 5, 1), 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd')
  };
}

function monthLabel(periodStart) {
  if (!periodStart) return '';
  const date = new Date(periodStart);
  if (Number.isNaN(date.getTime())) return String(periodStart).slice(0, 7);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function centsToDollars(cents) {
  return (Number(cents) || 0) / 100;
}

/** Hide, error, or empty Product vs Service — never keep the last window. */
export function financialSplitView(loading, split, splitError, metricsError) {
  if (loading) return 'loading';
  if (metricsError) return 'hidden';
  if (splitError) return 'error';
  if (!split) return 'empty';
  return 'ready';
}

/** Hide MRR / New vs Churn when the metrics fetch failed — never empty-window copy. */
export function financialTrendView(loading, metricsError, hasRows) {
  if (loading) return 'loading';
  if (metricsError) return 'hidden';
  if (!hasRows) return 'empty';
  return 'ready';
}

function FinancialReports({ user }) {
  const initial = lastSixMonthsRange();
  const [dateFrom, setDateFrom] = useState(initial.dateFrom);
  const [dateTo, setDateTo] = useState(initial.dateTo);
  const [metrics, setMetrics] = useState(null);
  const [split, setSplit] = useState(null);
  const [splitError, setSplitError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setMetrics(null);
      setSplit(null);
      setSplitError(null);
      const params = { dateFrom, dateTo };
      try {
        const [metricsOutcome, splitOutcome] = await Promise.all([
          reportingService.getRecurringMetrics(params).then(
            (data) => ({ ok: true, data }),
            (err) => ({ ok: false, err })
          ),
          reportingService.getRevenueSplit(params).then(
            (data) => ({ ok: true, data }),
            (err) => ({ ok: false, err })
          )
        ]);
        if (cancelled) return;
        if (!metricsOutcome.ok) {
          setError(getApiErrorMessage(metricsOutcome.err, 'Failed to load financial reports'));
          setMetrics(null);
          setSplit(null);
          setSplitError(null);
          return;
        }
        setMetrics(metricsOutcome.data);
        if (!splitOutcome.ok) {
          setSplit(null);
          setSplitError(getApiErrorMessage(splitOutcome.err, 'Failed to load product vs service split'));
          return;
        }
        setSplit(splitOutcome.data);
        setSplitError(null);
      } catch (err) {
        if (cancelled) return;
        setError(getApiErrorMessage(err, 'Failed to load financial reports'));
        setMetrics(null);
        setSplit(null);
        setSplitError(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [user?.id, dateFrom, dateTo]);

  const trend = metrics?.trend || [];
  const mrrTrendChart = trend.map((row) => ({
    ...row,
    label: monthLabel(row.periodStart),
    dollars: centsToDollars(row.mrrCents)
  }));
  const newChurnChart = trend.map((row) => ({
    ...row,
    label: monthLabel(row.periodStart),
    newDollars: centsToDollars(row.newAmountCents),
    churnDollars: centsToDollars(row.churnAmountCents)
  }));
  const splitChart = [
    { label: 'Product', dollars: centsToDollars(split?.productCents), color: CHART.brand },
    { label: 'Service', dollars: centsToDollars(split?.serviceCents), color: CHART.service }
  ];
  const mrrView = financialTrendView(loading, error, mrrTrendChart.length > 0);
  const newChurnView = financialTrendView(loading, error, newChurnChart.length > 0);
  const splitView = financialSplitView(loading, split, splitError, error);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <label className="text-sm font-medium text-gray-700">
          From
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 block border border-gray-300 rounded-lg px-3 py-2"
          />
        </label>
        <label className="text-sm font-medium text-gray-700">
          To
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 block border border-gray-300 rounded-lg px-3 py-2"
          />
        </label>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <MetricCard
          label="MRR"
          value={metrics ? formatCurrency(centsToDollars(metrics.mrrCents)) : '—'}
          hint={!loading && metrics ? `${metrics.activeCount || 0} active plans` : ''}
          loading={loading}
        />
        <MetricCard
          label="New"
          value={metrics ? String(metrics.newCount || 0) : '—'}
          hint={!loading && metrics ? formatCurrency(centsToDollars(metrics.newAmountCents)) : ''}
          loading={loading}
        />
        <MetricCard
          label="Churn"
          value={metrics ? String(metrics.churnCount || 0) : '—'}
          hint={!loading && metrics ? formatCurrency(centsToDollars(metrics.churnAmountCents)) : ''}
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 gap-6">
        <ChartCard
          title="MRR trend"
          subtitle="Month-end MRR from plan start, pause, cancel, and complete; last month is as of To"
        >
          {mrrView === 'loading' ? (
            <div className="flex h-full items-center justify-center text-3xl font-semibold text-gray-400" aria-busy="true">…</div>
          ) : mrrView === 'empty' ? (
            <ChartEmpty text="No recurring-plan history in this window." />
          ) : mrrView === 'ready' ? (
            <ReportingLineChart data={mrrTrendChart} dataKey="dollars" name="MRR" />
          ) : null}
        </ChartCard>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <ChartCard
            title="New vs Churn"
            subtitle="Monthly-normalized amounts"
            action={<ChartLegend items={[{ label: 'New', color: CHART.new }, { label: 'Churn', color: CHART.churn }]} />}
          >
            {newChurnView === 'loading' ? (
              <div className="flex h-full items-center justify-center text-3xl font-semibold text-gray-400" aria-busy="true">…</div>
            ) : newChurnView === 'empty' ? (
              <ChartEmpty text="No New or Churn in this window." />
            ) : newChurnView === 'ready' ? (
              <ReportingBarChart
                data={newChurnChart}
                bars={[
                  { dataKey: 'newDollars', name: 'New', color: CHART.new },
                  { dataKey: 'churnDollars', name: 'Churn', color: CHART.churn }
                ]}
              />
            ) : null}
          </ChartCard>

          <ChartCard
            title="Product vs Service"
            subtitle="Paid line totals in this window"
            action={<ChartLegend items={[{ label: 'Product', color: CHART.brand }, { label: 'Service', color: CHART.service }]} />}
          >
            {splitView === 'loading' ? (
              <div className="flex h-full items-center justify-center text-3xl font-semibold text-gray-400" aria-busy="true">…</div>
            ) : splitView === 'error' ? (
              <div className="flex h-full items-center justify-center text-sm text-red-800">{splitError}</div>
            ) : splitView === 'hidden' ? (
              null
            ) : splitView === 'empty' ? (
              <ChartEmpty text="No product vs service split for this window." />
            ) : (
              <ReportingBarChart
                data={splitChart}
                bars={[{ dataKey: 'dollars', name: 'Sales', color: CHART.brand }]}
              />
            )}
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, hint, loading }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{loading ? '…' : value}</div>
      {!loading && hint ? <div className="mt-1 text-sm text-gray-500">{hint}</div> : null}
    </div>
  );
}

export default FinancialReports;
